import React from "react";
import _ from "lodash";

/* usage:
questions = [
  {
    type: 'text',
    hint: ['question-display-content'], // could be multiple elements, random pick
    beforeAsk: async () => ({ next:"+1", value }), // will push value to acceptedAnswers if value is returned
    afterAnswer: async ({ value, question }) => ({ next, value, message })
    extra: {
      // may add type or other data for better display of UI Comp
    },
   {
     type: 'comment',
     hint: ['comment-display-content'],
     extra: {}
  }
]
 */

class Chatty extends React.Component {
  static defaultProps = {
    enableDefaultSuccessMessage: true,
    remoteThinkingMaxPeriod: 2500,
    defaultSuccessMessage: ["Awesome!", "Good job!", "Thanks!"],
    defaultFailureMessage: [
      "Oops! There're something wrong!",
      "Invalid answer, please retry!",
      "I don't understand, please try again!"
    ],
    defaultClosingMessage: [
      "All done!",
      "Cool, we are done!",
      "Congradulation! Our survey is completed"
    ],
    defaultInvalidChoiceMessage: [
      "No such option! Please retry!",
      "Invalid option! Please try again!"
    ]
  };

  constructor(props) {
    super(props);

    this.state = {
      messages: [],
      qIdx: 0,
      finished: false,
      acceptedAnswers: [],
      isRemoteThinking: true, // see componentDidMount
      isUserThinking: false
    };
  }

  async componentDidMount() {
    let { questions } = this.props;
    let {
      qIdx,
      acceptedAnswers,
      extraMessages
    } = await this._findNextQuestionIdxBeforeAsk({
      qIdx: 0,
      acceptedAnswers: this.state.acceptedAnswers
    });
    let messages = [];
    let finished = false;

    messages = [...messages, ...extraMessages];

    messages = messages.concat(
      this._createBotMessages({
        question: questions[qIdx],
        acceptedAnswers
      })
    );

    if (qIdx >= questions.length) {
      qIdx = questions.length - 1;
      finished = true;
    }

    //this._prevserveAndSetState({
    this.setState({
      qIdx,
      messages,
      isRemoteThinking: false,
      finished,
      acceptedAnswers
    });
  }

  render() {
    let {
      qIdx,
      messages,
      isRemoteThinking,
      isUserThinking,
      finished,
      acceptedAnswers
    } = this.state;
    let question = this.props.questions[qIdx];

    return this.props.children({
      chatty: {
        qIdx,
        messages,
        question,
        isRemoteThinking,
        isUserThinking,
        finished,
        acceptedAnswers,
        onAnswer: this._fnResp,
        revertLastAnswer: this._restoreToPrevState,
        isRevertable: () => {
          let currStateClone = _.clone(this.state);
          delete currStateClone.prevState;
          return !_.isEqual(currStateClone, this.state.prevState);
        }
      }
    });
  }

  _prevserveAndSetState = nextState => {
    if (!nextState) {
      nextState = _.clone(this.state);
      delete nextState.prevState;
    }

    this.setState({
      ...nextState,
      prevState: _.clone(nextState)
    });
  };

  _restoreToPrevState = () => {
    let prevState = this.state.prevState;

    this.setState({
      ...prevState,
      prevState
    });
  };

  _fnResp = async value => {
    this._prevserveAndSetState();

    let { qIdx, acceptedAnswers } = this.state;
    let {
      questions,
      defaultSuccessMessage,
      defaultFailureMessage,
      defaultClosingMessage,
      defaultInvalidChoiceMessage,
      enableDefaultSuccessMessage
    } = this.props;
    let question = questions[qIdx];
    let { afterAnswer } = question;
    let resp = enableDefaultSuccessMessage && defaultSuccessMessage;
    let userMsg = this._createUserMessage(question, value);
    let answerMsg = null;
    let nextMsgs = [],
      nextQuestionMsgs = [];
    let finished = false;

    this.setState({ isUserThinking: true });

    try {
      if (question.type === "select") {
        // if the question type is "select", we can always check if the value is a valid choice
        let optionIdx = parseInt(value);
        if (
          isNaN(optionIdx) ||
          optionIdx < 0 ||
          optionIdx >= question.choices.length
        ) {
          throw { errorMessage: defaultInvalidChoiceMessage };
        }
      }

      let validator = question.validate;
      if (validator) {
        let { message: respOverride } = await validator(value);
        if (respOverride) {
          resp = respOverride;
        }
      }

      let next = "+1";
      if (afterAnswer) {
        let {
          next: nextOverride,
          value: valueOverride,
          message: respOverride
        } = await afterAnswer({
          value,
          question
        });
        // when afterAnswer returns a value, we should override
        // the existing values
        if (nextOverride) {
          next = nextOverride;
        }
        if (valueOverride) {
          value = valueOverride;
          userMsg = this._createUserMessage(question, value);
        }
        if (respOverride) {
          resp = respOverride;
        }
      }

      let nextIdx = await this._findNextQuestionIdxAfterAnswer({ qIdx, next });

      // if we arrive here, means the answer has been accepted
      answerMsg = this._createUserMessage(question, value, { isAnswer: true });
      acceptedAnswers = [...acceptedAnswers, answerMsg];

      if (qIdx === questions.length - 1 && nextIdx === qIdx) {
        // we're currently answering the last question,
        // and after user's answer, the index doesn't jump back,
        // which means we have finished
        finished = true;
      } else {
        let extraMessages = [];

        ({
          qIdx,
          acceptedAnswers,
          extraMessages
        } = await this._findNextQuestionIdxBeforeAsk({
          qIdx: nextIdx,
          acceptedAnswers
        }));

        nextQuestionMsgs = [...nextQuestionMsgs, ...extraMessages];

        if (qIdx >= questions.length) {
          qIdx = questions.length - 1;
          finished = true;
        } else {
          nextQuestionMsgs = nextQuestionMsgs.concat(
            this._createBotMessages({
              question: questions[qIdx],
              acceptedAnswers
            })
          );
        }
      }
    } catch (ex) {
      if (typeof ex === "object" && ex.errorMessage) {
        resp = ex.errorMessage;
      } else {
        resp = defaultFailureMessage;
      }
    }

    if (resp) {
      nextMsgs = this._createBotMessages({ question, resp, acceptedAnswers });
    }

    this.setState({ isRemoteThinking: true, isUserThinking: false });

    await this._updateNewMessages([userMsg, ...nextMsgs, ...nextQuestionMsgs]);

    // next question
    this.setState({ qIdx, finished, isRemoteThinking: false, acceptedAnswers });
  };

  _findNextQuestionIdx = ({ qIdx, next }) => {
    let step = 1,
      relative = true;

    if (["+", "-"].indexOf(next[0]) === -1) {
      relative = false;
    }

    let s = parseInt(next);
    if (!isNaN(s)) {
      step = s;
    }

    let nextIdx;

    if (relative) {
      nextIdx = qIdx + step;
    } else {
      nextIdx = step;
    }

    return nextIdx;
  };

  _findNextQuestionIdxAfterAnswer = async ({ qIdx, next }) => {
    let { questions } = this.props;
    let nextIdx = this._findNextQuestionIdx({ qIdx, next });

    if (nextIdx > questions.length - 1) {
      nextIdx = questions.length - 1;
    } else if (nextIdx < 0) {
      nextIdx = 0;
    }

    return nextIdx;
  };

  _findNextQuestionIdxBeforeAsk = async ({ qIdx, acceptedAnswers = [] }) => {
    let { questions } = this.props;
    let extraMessages = [];

    acceptedAnswers = [...acceptedAnswers]; // explicitly make a copy

    while (true) {
      try {
        let question = questions[qIdx];
        let { beforeAsk, type } = question;

        if (type === "comment") {
          qIdx += 1;
          extraMessages = [
            ...extraMessages,
            ...this._createBotMessages({
              question,
              acceptedAnswers
            })
          ];
          continue;
        }

        if (beforeAsk) {
          let { next, value } = await beforeAsk();
          // update acceptedAnswers if beforeAsk return value
          if (typeof value !== "undefined" && value !== null) {
            extraMessages = [
              ...extraMessages,
              ...this._createBotMessages({
                question: questions[qIdx],
                acceptedAnswers
              }),
              this._createUserMessage(question, value, {
                isAnswer: true,
                beforeAsk: true
              })
            ];

            acceptedAnswers.push(
              this._createUserMessage(question, value, {
                isAnswer: true,
                beforeAsk: true
              })
            );
          }
          if (next) {
            qIdx = this._findNextQuestionIdx({ qIdx, next });
            continue;
          }
        }
        return { qIdx, acceptedAnswers, extraMessages };
      } catch (ex) {
        // if the qIdx passes over the last one, the code flow will still arrive here
        // because we cannot destructure beforeAsk in this case
        return { qIdx, acceptedAnswers, extraMessages };
      }
    }
  };

  _createBotMessages = ({ question, resp, acceptedAnswers = [] }) => {
    // if get blank string resp, do not show bot resp message
    if (resp === "") {
      return [];
    }
    // if we have resp then use it, otherwise it's a hint from question
    resp = resp || question.hint;

    let messages = [];
    let respValue = this._randomPick(resp);
    for (let line of respValue.split("\r\n")) {
      if (line) {
        messages.push({
          from: "bot",
          type: question.type,
          question,
          value: line,
          acceptedAnswers
        });
      }
    }

    return messages;
  };

  _createUserMessage = (
    question,
    value,
    options = { isAnswer: false, beforeAsk: false }
  ) => {
    const baseAttrs = {
      from: "user",
      type: question.type,
      question,
      beforeAsk: options.beforeAsk
    };
    if (question.type === "select") {
      let choice = question.choices[value];
      if (choice) {
        return {
          ...baseAttrs,
          value: choice.label
        };
      }
      return {
        ...baseAttrs,
        value: "..." // we don't have this option
      };
    }

    return {
      ...baseAttrs,
      value
    };
  };

  _randomPick = options => {
    if (Array.isArray(options)) {
      let idx = Math.floor(options.length * Math.random(options.length));
      return options[idx];
    }
    return options;
  };

  _updateNewMessages = async newMessages => {
    let { remoteThinkingMaxPeriod } = this.props;

    for (let msg of newMessages) {
      if (msg.from === "bot") {
        await this._delay(remoteThinkingMaxPeriod * Math.random());
      }
      this.setState(({ messages }) => ({
        messages: [...messages, msg]
      }));
    }
  };

  _delay = ms => new Promise(resolve => setTimeout(resolve, ms));
}

const Validator = {
  pass: () => async value => ({ message: ["Awesome!", "Great!"] }),
  fail: () => async value => {
    throw { errorMessage: ["No good!", "Shame on you!"] };
  },

  checkString: (options = { min: 5, max: 10 }) => async value => {
    let { min, max } = options;

    if (!value) {
      throw { errorMessage: ["wtf!", "This is not a name dude!"] };
    }
    if (value.length < min) {
      throw {
        errorMessage: [
          "too short",
          `you must endter a string more than ${min} characters`,
          "please give me a longer one"
        ]
      };
    }
    if (value.length >= max) {
      throw {
        errorMessage: [
          "too long",
          `you must endter a string less than ${max} characters`,
          "please give me a shorter one"
        ]
      };
    }
    return {};
  },

  checkInteger: (options = { min: 1, max: 100 }) => async value => {
    let { min, max } = options;

    value = parseInt(value, 10);
    if (isNaN(value) || value < min || value > max) {
      throw {
        errorMessage: [`You must give me a number between ${min} ~ ${max}`]
      };
    }
    return {};
  }
};

export default Chatty;
export { Validator };
